import { z } from 'zod';
import { InvoiceStatus, PaymentStatus, SubscriptionStatus, SubscriptionTier } from '../enums';

// ========================================
// Zod Schemas for Billing Enums
// ========================================

/** Zod schema for InvoiceStatus validation */
export const InvoiceStatusSchema = z.nativeEnum(InvoiceStatus);

/** Zod schema for PaymentStatus validation */
export const PaymentStatusSchema = z.nativeEnum(PaymentStatus);

/**
 * Subscription DTO Schema
 */
export const SubscriptionSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  tier: z.nativeEnum(SubscriptionTier),
  status: z.nativeEnum(SubscriptionStatus),
  stripeSubscriptionId: z.string().nullable().optional(),
  currentPeriodStart: z.date(),
  currentPeriodEnd: z.date(),
  cancelAtPeriodEnd: z.boolean().default(false),
  trialStart: z.date().nullable().optional(),
  trialEnd: z.date().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type Subscription = z.infer<typeof SubscriptionSchema>;

/**
 * Create Subscription DTO Schema
 */
export const CreateSubscriptionSchema = z.object({
  businessId: z.string().uuid(),
  tier: z.nativeEnum(SubscriptionTier),
  paymentMethodId: z.string().optional(), // Stripe payment method
});

export type CreateSubscription = z.infer<typeof CreateSubscriptionSchema>;

/**
 * Update Subscription DTO Schema
 */
export const UpdateSubscriptionSchema = z.object({
  tier: z.nativeEnum(SubscriptionTier).optional(),
  cancelAtPeriodEnd: z.boolean().optional(),
});

export type UpdateSubscription = z.infer<typeof UpdateSubscriptionSchema>;

/**
 * Usage Record DTO Schema
 */
export const UsageRecordSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  resourceType: z.enum(['tokens', 'documents', 'api_calls']),
  quantity: z.number().int().nonnegative(),
  timestamp: z.date(),
  metadata: z.record(z.string(), z.any()).optional(),
});

export type UsageRecord = z.infer<typeof UsageRecordSchema>;

/**
 * Usage Summary DTO Schema
 */
export const UsageSummarySchema = z.object({
  businessId: z.string().uuid(),
  period: z.object({
    start: z.date(),
    end: z.date(),
  }),
  tokens: z.object({
    used: z.number().int().nonnegative(),
    limit: z.number().int(), // -1 for unlimited
  }),
  documents: z.object({
    used: z.number().int().nonnegative(),
    limit: z.number().int(),
  }),
  apiCalls: z.object({
    used: z.number().int().nonnegative(),
    limit: z.number().int(),
  }),
});

export type UsageSummary = z.infer<typeof UsageSummarySchema>;

/**
 * Invoice DTO Schema
 */
export const InvoiceSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  stripeInvoiceId: z.string().nullable().optional(),
  amount: z.number().int().nonnegative(), // Amount in cents
  currency: z.string().length(3).default('usd'),
  status: z.enum(['draft', 'open', 'paid', 'void', 'uncollectible']),
  dueDate: z.date().nullable().optional(),
  paidAt: z.date().nullable().optional(),
  createdAt: z.date(),
});

export type Invoice = z.infer<typeof InvoiceSchema>;

/**
 * Payment Method DTO Schema
 */
export const PaymentMethodSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  stripePaymentMethodId: z.string(),
  type: z.enum(['card', 'bank_account']),
  last4: z.string().length(4),
  expiryMonth: z.number().int().min(1).max(12).nullable().optional(),
  expiryYear: z.number().int().nullable().optional(),
  isDefault: z.boolean().default(false),
  createdAt: z.date(),
});

export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

/**
 * Create Payment Method DTO Schema
 */
export const CreatePaymentMethodSchema = z.object({
  businessId: z.string().uuid(),
  stripePaymentMethodId: z.string(),
  isDefault: z.boolean().default(false),
});

export type CreatePaymentMethod = z.infer<typeof CreatePaymentMethodSchema>;

/**
 * Usage Limits by Subscription Tier
 */
export const USAGE_LIMITS = {
  free: {
    tokens: 10_000,
    documents: 5,
    api_calls: 100,
  },
  starter: {
    tokens: 50_000,
    documents: 25,
    api_calls: 1_000,
  },
  professional: {
    tokens: 500_000,
    documents: 100,
    api_calls: 10_000,
  },
  enterprise: {
    tokens: -1, // unlimited
    documents: -1,
    api_calls: -1,
  },
} as const;

export type UsageLimits = typeof USAGE_LIMITS;

// ========================================
// BILLING OVERVIEW DTOs
// ========================================

/**
 * Billing Overview DTO Schema
 * Returns aggregate billing metrics for dashboard
 */
export const BillingOverviewSchema = z.object({
  totalRevenue: z.number().nonnegative(),
  monthlyRecurringRevenue: z.number().nonnegative(),
  outstandingInvoices: z.number().int().nonnegative(),
  outstandingAmount: z.number().nonnegative(),
  paidInvoices: z.number().int().nonnegative(),
  overdueInvoices: z.number().int().nonnegative(),
  paymentSuccessRate: z.number().min(0).max(100),
  avgInvoiceAmount: z.number().nonnegative(),
  revenueGrowth: z.number(), // Can be negative for decline
});

export type BillingOverview = z.infer<typeof BillingOverviewSchema>;

/**
 * Revenue Trend Data Point Schema
 */
export const RevenueTrendPointSchema = z.object({
  month: z.string(),
  revenue: z.number().nonnegative(),
  invoiceCount: z.number().int().nonnegative().optional(),
});

export type RevenueTrendPoint = z.infer<typeof RevenueTrendPointSchema>;

/**
 * Revenue Trend Response Schema
 */
export const RevenueTrendResponseSchema = z.object({
  data: z.array(RevenueTrendPointSchema),
  period: z.object({
    start: z.date(),
    end: z.date(),
  }),
});

export type RevenueTrendResponse = z.infer<typeof RevenueTrendResponseSchema>;

/**
 * Billing Overview Query Params Schema
 */
export const BillingOverviewQuerySchema = z.object({
  dateRange: z.coerce.number().int().min(7).max(365).optional().default(30),
});

export type BillingOverviewQuery = z.infer<typeof BillingOverviewQuerySchema>;

// ========================================
// PAYMENT & INVOICE DTOs
// ========================================

// Re-export from enums for backwards compatibility
export { InvoiceStatus, PaymentStatus };

/**
 * Invoice Line Item Schema
 */
export const InvoiceItemSchema = z.object({
  id: z.string(),
  description: z.string(),
  quantity: z.number().int().positive(),
  unitPrice: z.number().nonnegative(),
  total: z.number().nonnegative(),
});

export type InvoiceItem = z.infer<typeof InvoiceItemSchema>;

/**
 * Full Invoice Schema (for API responses)
 */
export const FullInvoiceSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  businessName: z.string().optional(),
  invoiceNumber: z.string(),
  stripeInvoiceId: z.string().nullable().optional(),
  amount: z.number().int().nonnegative(),
  amountPaid: z.number().int().nonnegative().default(0),
  currency: z.string().length(3).default('usd'),
  status: InvoiceStatusSchema,
  issueDate: z.union([z.date(), z.string()]),
  dueDate: z.union([z.date(), z.string()]).nullable().optional(),
  paidAt: z.union([z.date(), z.string()]).nullable().optional(),
  items: z.array(InvoiceItemSchema).optional(),
  notes: z.string().nullable().optional(),
  createdAt: z.union([z.date(), z.string()]),
  updatedAt: z.union([z.date(), z.string()]),
});

export type FullInvoice = z.infer<typeof FullInvoiceSchema>;

/**
 * Create Invoice Schema
 */
export const CreateInvoiceSchema = z.object({
  businessId: z.string().uuid(),
  amount: z.number().int().positive(),
  currency: z.string().length(3).default('usd'),
  dueDate: z.union([z.date(), z.string()]).optional(),
  items: z.array(InvoiceItemSchema).optional(),
  notes: z.string().optional(),
});

export type CreateInvoice = z.infer<typeof CreateInvoiceSchema>;

/**
 * Update Invoice Schema
 */
export const UpdateInvoiceSchema = z.object({
  status: InvoiceStatusSchema.optional(),
  amount: z.number().int().positive().optional(),
  dueDate: z.union([z.date(), z.string()]).optional(),
  items: z.array(InvoiceItemSchema).optional(),
  notes: z.string().optional(),
});

export type UpdateInvoice = z.infer<typeof UpdateInvoiceSchema>;

/**
 * Send Invoice Schema
 * Used when transitioning invoice from draft to sent
 */
export const SendInvoiceSchema = z.object({
  notes: z.string().optional(),
  sendEmail: z.boolean().default(true),
});

export type SendInvoice = z.infer<typeof SendInvoiceSchema>;

/**
 * Mark Invoice Paid Schema
 * Used when marking an invoice as paid
 */
export const MarkInvoicePaidSchema = z.object({
  paymentMethodId: z.string().uuid().optional(),
  paymentReference: z.string().optional(),
  paidAt: z.union([z.date(), z.string()]).optional(),
  notes: z.string().optional(),
});

export type MarkInvoicePaid = z.infer<typeof MarkInvoicePaidSchema>;

/**
 * Invoice PDF Response Schema
 */
export const InvoicePdfResponseSchema = z.object({
  pdfUrl: z.string().url().nullable(),
  generatedAt: z.union([z.date(), z.string()]).nullable(),
  status: z.enum(['ready', 'generating', 'failed']),
});

export type InvoicePdfResponse = z.infer<typeof InvoicePdfResponseSchema>;

/**
 * Invoice List Query Schema
 */
export const InvoiceListQuerySchema = z.object({
  status: InvoiceStatusSchema.optional(),
  businessId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  offset: z.coerce.number().int().min(0).optional().default(0),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

export type InvoiceListQuery = z.infer<typeof InvoiceListQuerySchema>;

// ========================================
// BILLING ACCOUNT DTOs
// ========================================

/**
 * Billing Account Schema
 */
export const BillingAccountSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  stripeCustomerId: z.string().nullable().optional(),
  defaultPaymentMethodId: z.string().nullable().optional(),
  createdAt: z.union([z.date(), z.string()]),
  updatedAt: z.union([z.date(), z.string()]),
});

export type BillingAccount = z.infer<typeof BillingAccountSchema>;

/**
 * Add Payment Method Schema
 */
export const AddPaymentMethodSchema = z.object({
  stripePaymentMethodId: z.string(),
  isDefault: z.boolean().default(false),
});

export type AddPaymentMethod = z.infer<typeof AddPaymentMethodSchema>;

/**
 * Full Payment Method Schema (for API responses)
 */
export const FullPaymentMethodSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  stripePaymentMethodId: z.string(),
  type: z.enum(['card', 'bank_account']),
  brand: z.string().nullable().optional(),
  last4: z.string().length(4),
  expiryMonth: z.number().int().min(1).max(12).nullable().optional(),
  expiryYear: z.number().int().nullable().optional(),
  isDefault: z.boolean().default(false),
  createdAt: z.union([z.date(), z.string()]),
});

export type FullPaymentMethod = z.infer<typeof FullPaymentMethodSchema>;

/**
 * Payment Schema
 */
export const PaymentRecordSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  invoiceId: z.string().uuid().nullable().optional(),
  amount: z.number().int(),
  currency: z.string().length(3).default('usd'),
  status: PaymentStatusSchema,
  stripePaymentIntentId: z.string().nullable().optional(),
  failureReason: z.string().nullable().optional(),
  createdAt: z.union([z.date(), z.string()]),
});

export type PaymentRecord = z.infer<typeof PaymentRecordSchema>;

/**
 * Payment History Query Schema
 */
export const PaymentHistoryQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  offset: z.coerce.number().int().min(0).optional().default(0),
  status: PaymentStatusSchema.optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

export type PaymentHistoryQuery = z.infer<typeof PaymentHistoryQuerySchema>;

/**
 * Manual Charge Schema
 */
export const ManualChargeSchema = z.object({
  amount: z.number().int().positive(),
  currency: z.string().length(3).default('usd'),
  paymentMethodId: z.string().uuid().optional(),
  description: z.string().optional(),
  invoiceId: z.string().uuid().optional(),
});

export type ManualCharge = z.infer<typeof ManualChargeSchema>;
